import axios, { AxiosRequestConfig } from 'axios';
import { configure } from 'axios-hooks';
import { addSeconds, formatISO, isBefore, parseISO } from 'date-fns';
import React, { createContext, useEffect, useRef, useState } from 'react';
import { toast } from '~/components/Toaster';
import useDidUpdateEffect from '~/hooks/useDidUpdateEffect';
import {
	AccountResponse,
	LoginResponse,
	LoginVariables,
	RefreshResponse,
	RefreshVariables,
	RegisterResponse,
	RegisterVariables,
} from '~/models/api/auth';
import { TokenData, User } from '~/models/auth';
import api, { useMutation } from '~/utils/api';
import { bakeCookie, bakeLocalStorage, deleteCookie, readCookie } from '~/utils/storage';
import { useInvalidateQuery } from './api/requests/query';

axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*';

export type Auth = Partial<{
	user: User | null;
	isGoogleUser?: boolean;
	authenticated: boolean | null;
	isSuccess: boolean;
	accessToken?: string | null;
	refreshToken?: string | null;
	expires: {
		accessToken: Date | null;
		refreshToken: Date | null;
	};
	registerSubmitting: boolean;
	loginSubmitting: boolean;
	userLoading: boolean;
	isAdmin: boolean;
}> & {
	register: (data: RegisterVariables) => null | Promise<User | null>;
	login: (username: string, password: string, code?: string) => null | Promise<User | null>;
	logout: () => null | void;
	refresh: (refreshOptions?: { invalidateAccount: boolean }) => Promise<User> | null;
	update: (
		data: Pick<TokenData, 'accessToken' | 'refreshToken' | 'expiresIn' | 'refreshExpiresIn'>
	) => null | Promise<User | null>;
};

const MAX_DELAY = 2147483647;

export const AuthContext = createContext<Auth>({
	register: () => null,
	login: () => null,
	logout: () => null,
	refresh: () => null,
	update: () => null,
});

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const accessToken = useRef<string | null>(readCookie('accessToken') ?? null);
	const refreshToken = useRef<string | null>(readCookie('refreshToken') ?? null);
	const user = useRef<User | null>(null);

	const _expires = readCookie('user_expires');
	const [accessExpiresTimeout, setAccessExpiresTimeout] = useState<any>(null);
	const [refreshExpiresTimeout, setRefreshExpiresTimeout] = useState<any>(null);
	const [expires, setExpires] = useState<{
		accessToken: Date | null;
		refreshToken: Date | null;
	}>({
		accessToken: !!_expires?.accessToken ? parseISO(_expires.accessToken) : null,
		refreshToken: !!_expires?.refreshToken ? parseISO(_expires.refreshToken) : null,
	});

	const invalidate = useInvalidateQuery();

	const registerQuery = useMutation<RegisterResponse, RegisterVariables>(({ post }) => {
		return post(api.auth.register, {
			onSuccess: async ({
				accessToken: t,
				refreshToken: rt,
				expiresIn: accessExpires,
				refreshExpiresIn: refreshExpires,
			}) => {
				accessToken.current = t;
				refreshToken.current = rt;
				user.current = await accountQuery.mutateAsync();
				setExpires({
					accessToken: addSeconds(new Date(), accessExpires || 0),
					refreshToken: addSeconds(new Date(), refreshExpires || 0),
				});
			},
		});
	});

	const accountQuery = useMutation<AccountResponse>(({ get }) => get(api.auth.account));

	const loginQuery = useMutation<LoginResponse, LoginVariables>(({ post }) =>
		post(api.auth.login, {
			onSuccess: async ({
				accessToken: t,
				refreshToken: rt,
				expiresIn: accessExpires,
				refreshExpiresIn: refreshExpires,
			}) => {
				// here we rely on the returned data contains the user, the token and its expiration date.
				accessToken.current = t;
				refreshToken.current = rt;
				user.current = await accountQuery.mutateAsync();

				setExpires({
					accessToken: addSeconds(new Date(), accessExpires || 0),
					refreshToken: addSeconds(new Date(), refreshExpires || 0),
				});
			},
		})
	);

	const refreshQuery = useMutation<RefreshResponse, RefreshVariables>(({ post }) =>
		post(api.auth.refresh, {
			requestConfig: {
				headers: {
					Authorization: `Bearer ${refreshToken.current}`,
				},
			},
			onSuccess: async ({ accessToken: t, expiresIn: ae, ...d }) => {
				accessToken.current = t;
				user.current = await accountQuery.mutateAsync();

				setExpires({
					accessToken: addSeconds(new Date(), ae || 0),
					refreshToken: expires.refreshToken,
				});
			},
		})
	);

	const logout = () => {
		user.current = null;
		accessToken.current = null;
		refreshToken.current = null;

		// TODO: Joe/Jacob to make this work

		setExpires({
			accessToken: null,
			refreshToken: null,
		});
	};

	const login = async (email: string, password: string, code?: string) => {
		await loginQuery.mutateAsync({ email, password, code });

		bakeLocalStorage('user_previously_visited', {
			id: user?.current?._id,
			email: user?.current?.email,
			name: user?.current?.firstName,
		});

		// return logged in user, as it's more useful than token data
		return user.current;
	};

	// if an external request changes the user tokens, allow them to pass this data into auth to change the token data.
	// we will also fetch a new user account in case this data has changed
	const update = async ({
		accessToken: t,
		refreshToken: rt,
		expiresIn: accessExpires,
		refreshExpiresIn: refreshExpires,
	}: Pick<TokenData, 'accessToken' | 'refreshToken' | 'expiresIn' | 'refreshExpiresIn'>) => {
		accessToken.current = t || accessToken.current;
		refreshToken.current = rt || refreshToken.current;
		// fetch new user account (just in case it had changed)
		user.current = await accountQuery.mutateAsync();

		setExpires({
			accessToken: !!accessExpires
				? addSeconds(new Date(), accessExpires || 0)
				: expires.accessToken,
			refreshToken: !!refreshExpires
				? addSeconds(new Date(), refreshExpires || 0)
				: expires.refreshToken,
		});

		return user.current;
	};

	const refresh = async (
		{ invalidateAccount } = {
			invalidateAccount: true,
		}
	) => {
		if (invalidateAccount) invalidate(api.accounts.account);

		user.current = await accountQuery.mutateAsync();
		return user.current;
	};

	const register = async (data: RegisterVariables) => {
		await registerQuery.mutateAsync(data);
		// return logged in user, as it's more useful than token data
		return user.current;
	};

	// useEffect(() => {
	// 	if (!!user.current) return bakeLocalStorage('user', user.current);
	// 	deleteLocalStorage('user');
	// }, [user.current]);

	useEffect(() => {
		if (!!expires?.accessToken && !!expires?.refreshToken) {
			return bakeCookie(
				'user_expires',
				{
					accessToken: formatISO(expires?.accessToken),
					refreshToken: formatISO(expires?.refreshToken),
				},
				expires?.refreshToken
			);
		}

		deleteCookie('user_expires');

		return () => {
			clearTimeout(accessExpiresTimeout);
			clearTimeout(refreshExpiresTimeout);
		};
	}, [expires]);

	useEffect(() => {
		clearTimeout(accessExpiresTimeout);

		if (!expires?.accessToken) return;

		const accessExpiresTimeoutDelay = (expires?.accessToken as any) - Date.now();
		const maxDelay = 3600000; // 1 hour

		setAccessExpiresTimeout(
			setTimeout(
				() => {
					refreshQuery.mutate({
						email: (user.current as User)?.email,
						userId: (user.current as User)?._id,
						refreshToken: refreshToken.current as string,
					});
				},
				accessExpiresTimeoutDelay > maxDelay ? maxDelay : accessExpiresTimeoutDelay
			)
		);

		return () => {
			clearTimeout(accessExpiresTimeout);
		};
	}, [expires?.accessToken]);

	useEffect(() => {
		clearTimeout(refreshExpiresTimeout);
		if (!expires?.refreshToken) return;

		const refreshExpiresTimeoutDelay = (expires?.refreshToken as any) - Date.now();
		setRefreshExpiresTimeout(
			setTimeout(
				() => {
					logout();
					toast('Your session has expired, please log in again.');
				},
				refreshExpiresTimeoutDelay >= MAX_DELAY ? MAX_DELAY : refreshExpiresTimeoutDelay
			)
		);

		return () => {
			clearTimeout(refreshExpiresTimeout);
		};
	}, [expires?.refreshToken]);

	useEffect(() => {
		if (!!accessToken.current)
			return bakeCookie('accessToken', accessToken.current, expires?.accessToken);
		deleteCookie('accessToken');
	}, [accessToken.current, expires?.accessToken]);

	useEffect(() => {
		if (!!refreshToken.current)
			return bakeCookie('refreshToken', refreshToken.current, expires?.refreshToken);
		deleteCookie('refreshToken');
	}, [refreshToken.current, expires?.refreshToken]);

	useEffect(() => {
		// when an access token exists on initialisation, retrieve the account (we don't rely on local storage anymore).
		if (!!accessToken.current) {
			// the access token
			refresh();
		}
	}, [accessToken.current]);

	useEffect(() => {
		// if user exists but refresh token is missing, log the user out
		// this could happen if the cookies were deleted manually or by the browser, but the localStorage user still existed
		if (!!user.current && !refreshToken.current) {
			logout();
			toast('Your session has expired, please log in again.');
		}
	}, [user, accessToken, refreshToken]);

	useEffect(() => {
		// add authorization token to each request
		const headerIntercept = axios.interceptors.request.use(
			(config: AxiosRequestConfig): AxiosRequestConfig => {
				if (config.validateStatus !== null) {
					if (config.headers && !!accessToken.current && !config.headers.Authorization) {
						config.headers.Authorization = `Bearer ${accessToken.current}`;
					}
				}

				// this is important to include the cookies when we are sending the requests to the backend.
				// config.withCredentials = true;

				// prevent a user that no longer has a token from requesting any data (the token expired)
				if (!!expires?.refreshToken && isBefore(expires?.refreshToken, new Date()))
					logout();

				return config;
			}
		);

		const expiryIntercept = axios.interceptors.response.use(
			(response) => response,
			async (error) => {
				try {
					if (
						error.response?.data?.errorName === 'InvalidToken' ||
						error.response?.data?.errorName === 'TokenExpiredError' ||
						error.response?.data?.errorName === 'NoTokenProvided'
					) {
						const { accessToken: token } = await refreshQuery.mutateAsync({
							email: (user.current as User)?.email,
							userId: (user.current as User)?._id,
							refreshToken: refreshToken.current as string,
						});

						error.config.headers.Authorization = `Bearer ${token}`;
						return await axios(error.config);
					}
					throw error;
				} catch (error) {
					throw error;
				}
			}
		);

		// configure axios-hooks to use this instance of axios
		configure({ axios });

		return () => {
			// Eject interceptors so they can be re-created later with the new organisation
			axios.interceptors.request.eject(headerIntercept);
			axios.interceptors.response.eject(expiryIntercept);
		};
	}, []);

	useDidUpdateEffect(() => {}, []);

	// example on provider
	return (
		<AuthContext.Provider
			value={{
				isGoogleUser: user.hasOwnProperty('google'),
				user: user.current,
				isAdmin: user.current?.permissions?.admin ?? false,
				authenticated:
					!!user.current && !!refreshToken.current
						? true
						: accountQuery.isLoading || loginQuery.isLoading || registerQuery.isLoading
						  ? null
						  : false,
				accessToken: accessToken.current,
				refreshToken: refreshToken.current,
				expires,
				login,
				logout,
				update,
				refresh,
				register,
				registerSubmitting: registerQuery.isLoading,
				loginSubmitting: loginQuery.isLoading,
				userLoading:
					accountQuery.isLoading || loginQuery.isLoading || registerQuery.isLoading,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthProvider;
